import { Label } from "@/common/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Clock } from "lucide-react";
import DatePickerInput from "@/common/components/features/date-picker-input";
import { TabProps } from "../types";

interface TimelineTabProps extends TabProps {
  bidStartDate: string;
  bidEndDate: string;
  allotmentDate: string;
  refundDate: string;
  listingDate: string;
  setBidStartDate: (value: string) => void;
  setBidEndDate: (value: string) => void;
  setAllotmentDate: (value: string) => void;
  setRefundDate: (value: string) => void;
  setListingDate: (value: string) => void;
  convertToFormFormat: (dateStr: string) => string;
  validationErrors?: Record<string, string>;
}

const TimelineTab = ({
  bidStartDate,
  bidEndDate,
  allotmentDate,
  refundDate,
  listingDate,
  setBidStartDate,
  setBidEndDate,
  setAllotmentDate,
  setRefundDate,
  setListingDate,
  convertToFormFormat,
  validationErrors = {},
  readOnly = false,
}: TimelineTabProps) => {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <Clock className="h-4 w-4 text-primary" />
            IPO Timeline
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-x-12 gap-y-8 p-6 md:grid-cols-3">
        <div className="space-y-4">
          <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-primary">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            Subscription Phase
          </h4>
          <div className="space-y-3">
            {/* <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Anchor Bidding
              </Label>
              <DatePickerInput
                value={anchorBiddingDate}
                onChange={value => {
                  setAnchorBiddingDate(value);
                }}
                disabled={readOnly}
                placeholder={anchorBiddingDate || "Select date"}
                className={`h-10 border-emerald-600 bg-emerald-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-emerald-600 focus:bg-white ${
                  validationErrors.anchorBiddingDate
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              <input
                type="hidden"
                name="anchor_bidding_date"
                value={convertToFormFormat(anchorBiddingDate)}
              />
              {validationErrors.anchorBiddingDate && (
                <p className="text-xs text-red-600">
                  {validationErrors.anchorBiddingDate}
                </p>
              )}
            </div> */}
            <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Opening Date *
              </Label>
              <DatePickerInput
                value={bidStartDate}
                onChange={value => {
                  setBidStartDate(value);
                }}
                disabled={readOnly}
                placeholder={bidStartDate || "Select date"}
                className={`h-10 border-emerald-600 bg-emerald-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-emerald-600 focus:bg-white ${
                  validationErrors.bidStartDate
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              <input
                type="hidden"
                name="bid_start_date"
                value={convertToFormFormat(bidStartDate)}
                required
              />
              {validationErrors.bidStartDate && (
                <p className="text-xs text-red-600">
                  {validationErrors.bidStartDate}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Closing Date *
              </Label>
              <DatePickerInput
                value={bidEndDate}
                onChange={value => {
                  setBidEndDate(value);
                }}
                disabled={readOnly}
                placeholder={bidEndDate || "Select date"}
                className={`h-10 border-emerald-600 bg-emerald-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-emerald-600 focus:bg-white ${
                  validationErrors.bidEndDate
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              <input
                type="hidden"
                name="bid_end_date"
                value={convertToFormFormat(bidEndDate)}
                required
              />
              {validationErrors.bidEndDate && (
                <p className="text-xs text-red-600">
                  {validationErrors.bidEndDate}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-600">
            <div className="h-1.5 w-1.5 rounded-full bg-amber-600" />
            Allotment & Liquidity
          </h4>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Date of Allotment
              </Label>
              <DatePickerInput
                value={allotmentDate}
                onChange={value => {
                  setAllotmentDate(value);
                }}
                disabled={readOnly}
                placeholder="Select date"
                className="h-10 border-amber-600 bg-amber-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-amber-600 focus:bg-white"
              />
              <input
                type="hidden"
                name="allotment_date"
                value={convertToFormFormat(allotmentDate)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Refund Initiation
              </Label>
              <DatePickerInput
                value={refundDate}
                onChange={value => {
                  setRefundDate(value);
                }}
                disabled={readOnly}
                placeholder="Select date"
                className="h-10 border-amber-600 bg-amber-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-amber-600 focus:bg-white"
              />
              <input
                type="hidden"
                name="refund_date"
                value={convertToFormFormat(refundDate)}
              />
            </div>
            {/* <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Credit to Demat
              </Label>
              <DatePickerInput
                value={creditToDematDate}
                onChange={value => {
                  setCreditToDematDate(value);
                }}
                disabled={readOnly}
                placeholder="Select date"
                className="h-10 border-amber-600 bg-amber-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-amber-600 focus:bg-white"
              />
              <input
                type="hidden"
                name="credit_to_demat_date"
                value={convertToFormFormat(creditToDematDate)}
              />
            </div> */}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-600">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
            Market Debut
          </h4>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-bold uppercase text-slate-400">
                Listing Date
              </Label>
              <DatePickerInput
                value={listingDate}
                onChange={value => {
                  setListingDate(value);
                }}
                disabled={readOnly}
                placeholder={listingDate || "Select date"}
                className={`h-10 border-emerald-600 bg-emerald-600/10 text-sm placeholder:font-sans placeholder:text-sm placeholder:font-light placeholder:text-emerald-600 focus:bg-white ${
                  validationErrors.listingDate
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
              />
              <input
                type="hidden"
                name="listing_date"
                value={convertToFormFormat(listingDate)}
              />
              {validationErrors.listingDate && (
                <p className="text-xs text-red-600">
                  {validationErrors.listingDate}
                </p>
              )}
            </div>
            <div className="mt-8 flex flex-col gap-2 rounded-xl bg-slate-900 p-4 text-white shadow-xl">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-primary/70" />
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Live Status Sync
                </span>
              </div>
              <p className="text-[11px] font-medium leading-relaxed text-slate-300">
                Timeline updates are reflected in real-time on the investor
                dashboard.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TimelineTab;
